/**
 * Per-thread expand state for folded Work progress notes. Opening a fold
 * keeps later notes in that group visible so someone who starts reading
 * along can keep reading.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const WorkNotesVisibilityContext = createContext({
  isFoldOpen: () => false,
  toggleFold: () => {},
});

export function WorkNotesVisibilityProvider({ children }) {
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const isFoldOpen = useCallback((key) => {
    if (!key) return false;
    return openKeys.has(key);
  }, [openKeys]);

  const toggleFold = useCallback((key) => {
    if (!key) return;
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ isFoldOpen, toggleFold }), [isFoldOpen, toggleFold]);

  return (
    <WorkNotesVisibilityContext.Provider value={value}>
      {children}
    </WorkNotesVisibilityContext.Provider>
  );
}

WorkNotesVisibilityProvider.propTypes = {
  children: PropTypes.node,
};

export function useWorkNotesVisibility() {
  return useContext(WorkNotesVisibilityContext);
}
